import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient, { API_ENDPOINTS } from '../api/config';
import {
  clearAuthSession,
  getActiveAuthStorage,
  getStoredAuthToken,
  getStoredAuthUserJson,
  setAuthSession,
  syncStoredAuthUser,
} from '../utils/authStorage';

const TEMP_PASSWORD_RESET_FLAG = 'tempPasswordResetRequired';
const TEMP_PASSWORD_RESET_EMAIL = 'tempPasswordResetEmail';
const MUST_CHANGE_PASSWORD_FLAG = 'mustChangePassword';
const PENDING_MFA_CHALLENGE = 'pendingMfaChallenge';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  can_view_logs?: boolean;
  must_change_password?: boolean;
  mfa_required?: boolean;
  mfa_verified?: boolean;
  organization_id?: number;
  organization_name?: string;
  organization_role?: string;
  active_organization_id?: number;
  organizations?: Array<{
    id: number;
    name: string;
    role: string;
  }>;
}

interface PendingMfaChallenge {
  challenge_token: string;
  expires_at?: string;
  email: string;
  rememberMe: boolean;
  user?: User | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  mustChangePassword: boolean;
  pendingMfa: PendingMfaChallenge | null;
  mfaRequired: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ must_change_password?: boolean; mfa_required?: boolean }>;
  register: (name: string, username: string, email: string, password: string) => Promise<{ message: string; delivery_status: string; email?: string }>;
  forgotPassword: (email: string) => Promise<{ message: string }>;
  resendVerification: (email: string) => Promise<{ message: string; delivery_status: string; email?: string }>;
  verifyEmail: (token: string) => Promise<{ must_change_password?: boolean; is_superadmin?: boolean; mfa_required?: boolean }>;
  verifyMfa: (otpCode: string, trustDevice?: boolean, deviceName?: string) => Promise<{ must_change_password?: boolean; is_superadmin?: boolean }>;
  resendMfa: () => Promise<{ message: string; delivery_status?: string }>;
  clearPendingMfa: () => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  selectOrganization: (
    organizationId: number,
    organization?: {
      id: number;
      name: string;
      role?: string;
    },
  ) => void;
  clearOrganizationSelection: () => void;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readPendingMfaChallenge(): PendingMfaChallenge | null {
  try {
    const raw = sessionStorage.getItem(PENDING_MFA_CHALLENGE);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.challenge_token || !parsed?.email) {
      return null;
    }
    return {
      challenge_token: String(parsed.challenge_token),
      expires_at: parsed.expires_at ? String(parsed.expires_at) : undefined,
      email: String(parsed.email).trim().toLowerCase(),
      rememberMe: Boolean(parsed.rememberMe),
      user: parsed.user || null,
    };
  } catch {
    return null;
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const normalizeUser = (candidate: User | null): User | null => {
      if (!candidate) {
        return null;
      }

      const organizations = Array.isArray(candidate.organizations) ? candidate.organizations : [];
      const normalizedRole = String(candidate.role || '').trim().toLowerCase();
      const isSuperAdmin = normalizedRole === 'superadmin';
      const selectedOrganizationId = Number(
        candidate.active_organization_id || candidate.organization_id || (isSuperAdmin ? 0 : organizations[0]?.id) || 0
      );
      const selectedOrganization = selectedOrganizationId
        ? organizations.find((organization) => organization.id === selectedOrganizationId)
        : undefined;

      return {
        ...candidate,
        organization_id: selectedOrganizationId || undefined,
        active_organization_id: selectedOrganizationId || undefined,
        organization_name: selectedOrganization?.name || candidate.organization_name || undefined,
        organization_role: selectedOrganization?.role || (!isSuperAdmin ? candidate.organization_role : undefined),
      };
    };

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(
    () => localStorage.getItem(MUST_CHANGE_PASSWORD_FLAG) === 'true'
  );
  const [pendingMfa, setPendingMfa] = useState<PendingMfaChallenge | null>(() => readPendingMfaChallenge());

  const applyMustChangePassword = (required: boolean) => {
    if (required) {
      localStorage.setItem(MUST_CHANGE_PASSWORD_FLAG, 'true');
      sessionStorage.setItem(MUST_CHANGE_PASSWORD_FLAG, 'true');
    } else {
      localStorage.removeItem(MUST_CHANGE_PASSWORD_FLAG);
      sessionStorage.removeItem(MUST_CHANGE_PASSWORD_FLAG);
    }
    setMustChangePassword(required);
  };

  const applyTempPasswordResetFlow = (required: boolean, email?: string) => {
    if (required) {
      sessionStorage.setItem(TEMP_PASSWORD_RESET_FLAG, 'true');
      if (email) {
        sessionStorage.setItem(TEMP_PASSWORD_RESET_EMAIL, String(email).trim().toLowerCase());
      }
    } else {
      sessionStorage.removeItem(TEMP_PASSWORD_RESET_FLAG);
      sessionStorage.removeItem(TEMP_PASSWORD_RESET_EMAIL);
    }
  };

  const syncStoredUser = (updatedUser: User | null) => {
    const serializedUser = updatedUser ? JSON.stringify(updatedUser) : null;
    syncStoredAuthUser(serializedUser);
  };

  const savePendingMfa = (challenge: PendingMfaChallenge | null) => {
    if (challenge) {
      sessionStorage.setItem(PENDING_MFA_CHALLENGE, JSON.stringify(challenge));
    } else {
      sessionStorage.removeItem(PENDING_MFA_CHALLENGE);
    }
    setPendingMfa(challenge);
  };

  const clearPendingMfa = () => {
    savePendingMfa(null);
  };

  useEffect(() => {
    // Check if user is logged in on app start
    const validateToken = async () => {
      const token = getStoredAuthToken();
      const userData = getStoredAuthUserJson();
      
      if (token && userData) {
        try {
          // Verify token with backend and restore any enforced password-change state
          const response = await apiClient.get(API_ENDPOINTS.ME);
          const parsedUser = JSON.parse(userData);
          const backendUser = response.data || {};
          if (backendUser?.mfa_required && !backendUser?.must_change_password) {
            throw new Error('MFA verification is required for this session.');
          }
          const parsedUserId = parsedUser?.id != null ? String(parsedUser.id) : null;
          const backendUserId = backendUser?.id != null ? String(backendUser.id) : null;
          const parsedEmail = String(parsedUser?.email || '').trim().toLowerCase();
          const backendEmail = String(backendUser?.email || '').trim().toLowerCase();

          if (
            (parsedUserId && backendUserId && parsedUserId !== backendUserId) ||
            (parsedEmail && backendEmail && parsedEmail !== backendEmail)
          ) {
            throw new Error('Stored auth user does not match the active token.');
          }

          // Preserve the client-side org selection (set via sidebar org switcher) if it is
          // a valid membership for this user.  The backend /me endpoint always returns the
          // DB-stored primary org, which would silently override a manual selection.
          const localActiveOrgId = Number(parsedUser?.active_organization_id || 0) || null;
          const localUserIsSuperAdmin = String(parsedUser?.role || '').trim().toLowerCase() === 'superadmin';
          const availableOrgs: Array<{ id: number }> = Array.isArray(backendUser.organizations)
            ? backendUser.organizations
            : (Array.isArray(parsedUser.organizations) ? parsedUser.organizations : []);
          const isValidLocalSelection = localActiveOrgId !== null
            && (
              localUserIsSuperAdmin
              || availableOrgs.some((org) => org.id === localActiveOrgId)
            );

          const mergedUser = {
            ...parsedUser,
            ...backendUser,
            // Only override the backend's value when the local selection is explicitly valid.
            ...(isValidLocalSelection ? { active_organization_id: localActiveOrgId } : {}),
          };

          const restoredUser = normalizeUser(mergedUser);
          const passwordChangeRequired = Boolean(
            backendUser?.must_change_password ??
            restoredUser?.must_change_password ??
            localStorage.getItem(MUST_CHANGE_PASSWORD_FLAG) === 'true'
          );

          getActiveAuthStorage()?.setItem('user', JSON.stringify(restoredUser));
          setUser(restoredUser);
          applyMustChangePassword(passwordChangeRequired);
        } catch (error) {
          // Token is invalid, clear it
          console.error('Token validation failed:', error);
          clearAuthSession();
          localStorage.removeItem(MUST_CHANGE_PASSWORD_FLAG);
          sessionStorage.removeItem(MUST_CHANGE_PASSWORD_FLAG);
        }
      }
      setLoading(false);
    };

    void validateToken();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<{ must_change_password?: boolean; mfa_required?: boolean }> => {
    try {
      // Always use backend login, even for admin
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        email,
        password
      });
      
      const {
        access_token,
        user: userData,
        must_change_password,
        mfa_required,
        mfa_challenge_token,
        mfa_expires_at,
      } = response.data;
      const normalizedEmail = String(email).trim().toLowerCase();

      if (mfa_required) {
        if (!mfa_challenge_token) {
          throw new Error('MFA challenge was not issued. Please try signing in again.');
        }
        clearAuthSession();
        applyTempPasswordResetFlow(false);
        applyMustChangePassword(false);
        const pendingUser = normalizeUser({ ...(userData || {}), mfa_required: true });
        savePendingMfa({
          challenge_token: String(mfa_challenge_token || ''),
          expires_at: mfa_expires_at,
          email: String(userData?.email || normalizedEmail).trim().toLowerCase(),
          rememberMe,
          user: pendingUser,
        });
        setUser(null);
        return { mfa_required: true, must_change_password: false };
      }

      if (!access_token) {
        throw new Error('Login did not return an access token.');
      }

      const normalizedUser = normalizeUser({ ...userData, must_change_password: !!must_change_password, mfa_verified: true });

      applyTempPasswordResetFlow(!!must_change_password, normalizedEmail);
      applyMustChangePassword(!!must_change_password);
      clearPendingMfa();

      setAuthSession(rememberMe ? 'local' : 'session', access_token, JSON.stringify(normalizedUser));
      
      setUser(normalizedUser);
      return { must_change_password: !!must_change_password };
    } catch (error) {
      throw error;
    }
  };

  const register = async (name: string, username: string, email: string, password: string): Promise<{ message: string; delivery_status: string; email?: string }> => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.REGISTER, {
        full_name: name,
        username,
        email,
        password
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (email: string): Promise<{ message: string }> => {
    try {
      const normalizedEmail = String(email).trim().toLowerCase();
      const response = await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, { email: normalizedEmail });
      applyTempPasswordResetFlow(true, normalizedEmail);
      return {
        message: response.data?.message || 'If that email is registered, a temporary password has been sent.'
      };
    } catch (error) {
      throw error;
    }
  };

  const resendVerification = async (email: string): Promise<{ message: string; delivery_status: string; email?: string }> => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.RESEND_VERIFICATION, { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const verifyEmail = async (token: string): Promise<{ must_change_password?: boolean; is_superadmin?: boolean; mfa_required?: boolean }> => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.VERIFY_EMAIL, { params: { token } });
      const {
        access_token,
        user: userData,
        must_change_password,
        mfa_required,
        mfa_challenge_token,
        mfa_expires_at,
      } = response.data;
      const normalizedUser = normalizeUser({ ...(userData || {}), must_change_password: !!must_change_password });

      if (mfa_required) {
        if (!mfa_challenge_token) {
          throw new Error('MFA challenge was not issued. Please try signing in again.');
        }
        clearAuthSession();
        applyMustChangePassword(false);
        applyTempPasswordResetFlow(false);
        savePendingMfa({
          challenge_token: String(mfa_challenge_token || ''),
          expires_at: mfa_expires_at,
          email: String(userData?.email || '').trim().toLowerCase(),
          rememberMe: true,
          user: normalizedUser,
        });
        setUser(null);
        return {
          mfa_required: true,
          is_superadmin: /superadmin/i.test(String(normalizedUser?.role || userData?.role || '')),
        };
      }

      if (!access_token) {
        throw new Error('Email verification did not return an access token.');
      }

      applyMustChangePassword(!!must_change_password);
      applyTempPasswordResetFlow(!!must_change_password, normalizedUser?.email);
      clearPendingMfa();

      setAuthSession('local', access_token, JSON.stringify(normalizedUser));

      setUser(normalizedUser);
      return {
        must_change_password: !!must_change_password,
        is_superadmin: /superadmin/i.test(String(normalizedUser.role || userData?.role || '')),
      };
    } catch (error) {
      throw error;
    }
  };

  const verifyMfa = async (otpCode: string, trustDevice: boolean = false, deviceName?: string): Promise<{ must_change_password?: boolean; is_superadmin?: boolean }> => {
    if (!pendingMfa?.challenge_token) {
      throw new Error('MFA challenge has expired. Please sign in again.');
    }

    const response = await apiClient.post(API_ENDPOINTS.MFA_VERIFY, {
      challenge_token: pendingMfa.challenge_token,
      otp_code: otpCode,
      trust_device: trustDevice,
      device_name: deviceName,
    });

    const { access_token, user: userData, must_change_password } = response.data;
    if (!access_token) {
      throw new Error('MFA verification did not return an access token.');
    }

    const normalizedUser = normalizeUser({ ...userData, must_change_password: !!must_change_password, mfa_verified: true });
    applyTempPasswordResetFlow(!!must_change_password, normalizedUser?.email);
    applyMustChangePassword(!!must_change_password);
    clearPendingMfa();
    setAuthSession(pendingMfa.rememberMe ? 'local' : 'session', access_token, JSON.stringify(normalizedUser));
    setUser(normalizedUser);

    return {
      must_change_password: !!must_change_password,
      is_superadmin: /superadmin/i.test(String(normalizedUser?.role || userData?.role || '')),
    };
  };

  const resendMfa = async (): Promise<{ message: string; delivery_status?: string }> => {
    if (!pendingMfa?.challenge_token) {
      throw new Error('MFA challenge has expired. Please sign in again.');
    }

    const response = await apiClient.post(API_ENDPOINTS.MFA_RESEND, {
      challenge_token: pendingMfa.challenge_token,
    });
    const nextChallenge = response.data || {};
    savePendingMfa({
      ...pendingMfa,
      challenge_token: String(nextChallenge.mfa_challenge_token || pendingMfa.challenge_token),
      expires_at: nextChallenge.mfa_expires_at || pendingMfa.expires_at,
    });
    return {
      message: nextChallenge.message || 'Verification code sent.',
      delivery_status: nextChallenge.delivery_status,
    };
  };

  const clearMustChangePassword = () => {
    applyMustChangePassword(false);
  };

  const revokeStoredToken = (token: string): void => {
    const payload = JSON.stringify({ token });
    const baseUrl = String(apiClient.defaults.baseURL || '').replace(/\/$/, '');
    const logoutUrl = `${baseUrl}${API_ENDPOINTS.LOGOUT}`;

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const body = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(logoutUrl, body)) {
        return;
      }
    }

    void apiClient.post(API_ENDPOINTS.LOGOUT, { token }).catch(() => undefined);
  };

  const logout = () => {
    const token = getStoredAuthToken();
    if (token) {
      revokeStoredToken(token);
    }
    clearAuthSession();
    applyTempPasswordResetFlow(false);
    applyMustChangePassword(false);
    clearPendingMfa();
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = normalizeUser({ ...user, ...userData });
      setUser(updatedUser);
      syncStoredAuthUser(JSON.stringify(updatedUser));
    }
  };

  const selectOrganization = (
    organizationId: number,
    organization?: {
      id: number;
      name: string;
      role?: string;
    },
  ) => {
    if (!user) {
      return;
    }

    const currentOrganizationId = Number(user.active_organization_id || user.organization_id || 0);
    if (currentOrganizationId === organizationId) {
      return;
    }

    const organizations = Array.isArray(user.organizations) ? user.organizations : [];
    const isSuperAdmin = String(user.role || '').trim().toLowerCase() === 'superadmin';
    const selectedOrganization = organizations.find((item) => item.id === organizationId)
      || (organization ? {
        id: organization.id,
        name: organization.name,
        role: organization.role || 'superadmin',
      } : undefined);
    if (!selectedOrganization) {
      return;
    }

    const updatedUser = normalizeUser({
      ...user,
      active_organization_id: selectedOrganization.id,
      organization_name: selectedOrganization.name,
      organization_role: isSuperAdmin ? undefined : selectedOrganization.role,
      organization_id: isSuperAdmin ? undefined : selectedOrganization.id,
    });

    setUser(updatedUser);
    syncStoredUser(updatedUser);
    window.location.reload();
  };

  const clearOrganizationSelection = () => {
    if (!user) {
      return;
    }

    const isSuperAdmin = String(user.role || '').trim().toLowerCase() === 'superadmin';
    if (!isSuperAdmin) {
      return;
    }

    const updatedUser = normalizeUser({
      ...user,
      organization_id: undefined,
      active_organization_id: undefined,
      organization_name: undefined,
      organization_role: undefined,
    });

    setUser(updatedUser);
    syncStoredUser(updatedUser);
    window.location.reload();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    mustChangePassword,
    pendingMfa,
    mfaRequired: !!pendingMfa,
    login,
    register,
    forgotPassword,
    resendVerification,
    verifyEmail,
    verifyMfa,
    resendMfa,
    clearPendingMfa,
    logout,
    updateUser,
    selectOrganization,
    clearOrganizationSelection,
    clearMustChangePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
